import React, { useEffect, useRef, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { database } from "../../../Services/firebase";
import { getSummaryCount } from "../../../Services/api";

const SHIFT_COLORS = ["#3d7a50", "#305878", "#7a6030", "#7a3030", "#5a3878", "#3a5878"];
const STATE_COLORS = { Active: "#3d7a50", Inactive: "#7a3030" };
const OTHER_COLORS = ["#7a6030", "#305878", "#5a3878"];

function drawDonut(canvas, segments, total, centerTop, centerBottom) {
  if (!canvas) return;
  const dpr = window.devicePixelRatio || 2;
  const W = 108;
  const cx = W / 2;
  const cy = W / 2;
  const R = 35;
  const lw = 13;

  canvas.width = W * dpr;
  canvas.height = W * dpr;
  canvas.style.width = W + "px";
  canvas.style.height = W + "px";

  const ctx = canvas.getContext("2d");
  ctx.scale(dpr, dpr);

  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(255,255,255,0.06)";
  ctx.lineWidth = lw;
  ctx.stroke();

  if (total > 0) {
    let ang = -Math.PI / 2;
    segments.forEach((seg) => {
      if (!seg.v) return;
      const a = (seg.v / total) * Math.PI * 2;
      ctx.beginPath();
      ctx.arc(cx, cy, R, ang, ang + a - 0.025);
      ctx.strokeStyle = seg.c;
      ctx.lineWidth = lw;
      ctx.lineCap = "butt";
      ctx.stroke();
      ang += a;
    });
  }

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#e8e8e8";
  ctx.font = "bold 17px system-ui,sans-serif";
  ctx.fillText(centerTop, cx, cy - 5);
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.font = "10px system-ui,sans-serif";
  ctx.fillText(centerBottom, cx, cy + 9);
}

function getStateColor(state, otherIndex) {
  return STATE_COLORS[state] || OTHER_COLORS[otherIndex % OTHER_COLORS.length];
}

function ShiftAttendance({ stateMap, accessToken, allowedZones }) {
  const shiftRef = useRef(null);
  const locRef   = useRef(null);
  const [shifts, setShifts] = useState(null);
  const [error,  setError]  = useState(false);

  // Build sorted state entries (only non-zero)
  const stateEntries = stateMap
    ? Object.entries(stateMap)
        .filter(([, count]) => count > 0)
        .map(([state, count], i) => ({
          state,
          count,
          color: getStateColor(state, i),
        }))
    : [];
  const locTotal  = stateEntries.reduce((s, e) => s + e.count, 0);
  const openCount = stateMap?.["Active"] || 0;

  useEffect(() => {
    async function load() {
      try {
        const opSnap = await getDoc(doc(database, "config", "operations"));
        const shiftDefs = opSnap.data()?.shifts || [];
        if (!shiftDefs.length) { setShifts([]); return; }

        const today = new Date();
        const results = await Promise.all(
          shiftDefs.map(async (shift, i) => {
            const [sh, sm] = shift.from.split(":").map(Number);
            const [eh, em] = shift.to.split(":").map(Number);
            const start = new Date(today); start.setHours(sh, sm, 0, 0);
            const end   = new Date(today); end.setHours(eh, em, 59, 999);
            const response = await getSummaryCount({
              accessToken,
              collection: "captures",
              uniqueKey: "employee.id",
              type: "uniqueCount",
              query: {
                count: [
                  "createdAt", ">=", +start,
                  "createdAt", "<=", +end,
                  "success", "==", true,
                  ...(Array.isArray(allowedZones) && allowedZones.length > 0
                    ? ["location.zone", "in", allowedZones]
                    : []),
                ],
              },
            });
            return {
              label: `Shift ${i + 1}`,
              from: shift.from,
              to: shift.to,
              count: response.count,
              color: SHIFT_COLORS[i % SHIFT_COLORS.length],
            };
          })
        );
        setShifts(results);
      } catch (e) {
        console.error(e);
        setError(true);
        setShifts([]);
      }
    }
    load();
  }, [accessToken, allowedZones]);

  // Draw shift donut
  useEffect(() => {
    if (!shifts?.length) return;
    const total = shifts.reduce((s, sh) => s + sh.count, 0);
    drawDonut(
      shiftRef.current,
      shifts.map((sh) => ({ v: sh.count, c: sh.color })),
      total || 1,
      total,
      "Captures"
    );
  }, [shifts]);

  // Draw locations donut
  useEffect(() => {
    if (!stateEntries.length) return;
    drawDonut(
      locRef.current,
      stateEntries.map((e) => ({ v: e.count, c: e.color })),
      locTotal || 1,
      locTotal,
      "PCTs"
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateMap]);

  const shiftTotal = shifts?.reduce((s, sh) => s + sh.count, 0) ?? 0;

  return (
    <div data-cy="shift-attendance">
      <div className="sec-hdr">
        <div className="t-section">Attendance &amp; PCTs</div>
      </div>

      <div style={{ padding: "14px 20px 12px", display: "flex" }}>

        {/* ── Left: Shift Attendance ── */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ fontSize: "var(--text-xs)", color: "var(--t3)", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: "7px" }}>
            Captures
          </div>

          {shifts === null ? (
            <div style={{ width: "108px", height: "108px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: "var(--text-md)", color: "var(--t3)" }}>Loading…</span>
            </div>
          ) : shifts.length === 0 ? (
            <div style={{ width: "108px", height: "108px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: "var(--text-sm)", color: "var(--t3)", textAlign: "center" }}>
                {error ? "Error" : "No shifts"}
              </span>
            </div>
          ) : (
            <canvas ref={shiftRef} width="108" height="108" style={{ display: "block" }} />
          )}

          {shifts?.length > 0 && (
            <div style={{ marginTop: "8px", width: "100%", padding: "0 4px" }}>
              {shifts.map((sh) => (
                <div key={sh.label} style={{ display: "flex", alignItems: "center", gap: "5px", marginBottom: "4px" }}>
                  <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: sh.color, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "var(--text-xs)", color: "var(--t2)", fontWeight: 500 }}>{sh.label}</div>
                    <div style={{ fontSize: "var(--text-xs)", color: "var(--t3)", fontFamily: "var(--font-mono)" }}>{sh.from}–{sh.to}</div>
                  </div>
                  <div style={{ fontSize: "var(--text-sm)", fontWeight: 700, color: "var(--t1)", fontFamily: "var(--font-mono)" }}>
                    {sh.count}
                  </div>
                </div>
              ))}
              <div style={{ marginTop: "4px", fontSize: "var(--text-xs)", color: "var(--t3)", textAlign: "right", fontFamily: "var(--font-mono)" }}>
                total {shiftTotal}
              </div>
            </div>
          )}
        </div>

        {/* ── Right: PCT States ── */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ fontSize: "var(--text-xs)", color: "var(--t3)", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: "7px" }}>
            PCT
          </div>

          {!stateMap ? (
            <div style={{ width: "108px", height: "108px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: "var(--text-md)", color: "var(--t3)" }}>Loading…</span>
            </div>
          ) : (
            <canvas ref={locRef} width="108" height="108" style={{ display: "block" }} />
          )}

          {stateEntries.length > 0 && (
            <div style={{ marginTop: "8px", width: "100%", padding: "0 4px" }}>
              {stateEntries.map((e) => (
                <div key={e.state} style={{ display: "flex", alignItems: "center", gap: "5px", marginBottom: "4px" }}>
                  <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: e.color, flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: "var(--text-xs)", color: "var(--t2)", fontWeight: 500 }}>{e.state}</span>
                  <span style={{ fontSize: "var(--text-sm)", fontWeight: 700, color: "var(--t1)", fontFamily: "var(--font-mono)" }}>
                    {e.count}
                  </span>
                </div>
              ))}
              <div style={{ marginTop: "4px", fontSize: "var(--text-xs)", color: "var(--t3)", textAlign: "right", fontFamily: "var(--font-mono)" }}>
                {openCount} PCTs open
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default ShiftAttendance;
